import { cart } from '../helpers/cart'

/**
 * EnhancedProductForm - Custom HTML Form Element for product purchases and back-in-stock notifications
 *
 * Handles both regular product add-to-cart forms and Klaviyo back-in-stock notifications
 * with automatic detection and switching between modes.
 */
export class EnhancedProductForm extends HTMLElement {
  private form: HTMLFormElement
  // Form configuration
  private config = {
    isKlaviyoForm: false,
    apiKey: '',
    listId: '',
    apiRevision: '2025-01-15',
    endpoint: 'https://a.klaviyo.com/client/subscriptions',
    formType: 'custom',
    variantId: '',
    productTitle: ''
  }

  // UI uiElements
  private uiElements = {
    wrapper: null as HTMLElement | HTMLFormElement | Element | null,
    success: null as HTMLElement | null,
    error: null as HTMLElement | null
  }

  // CSS classes and selectors
  private ui = {
    selectors: {
      showOnSuccess: '[data-show-on-success]',
      hideOnSuccess: '[data-hide-on-success]',
      errorMessage: '[data-error-message]',
      successMessage: '[data-success-message]',
      uiAttribute: 'data-ui'
    },
    classes: {
      visible: 'visible',
      errorField: 'form-error',
      errorClass: 'form-error w-full paragraph-s font-semibold text-type-error'
    }
  }

  // Field validators
  // eslint-disable-next-line func-call-spacing
  private validators = new Map<string, (value: string) => boolean>([
    [
      'email',
      (value: string) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        // Check for both valid email format and printable ASCII characters
        return emailRegex.test(value) && /^[\x20-\x7F]*$/.test(value)
      }
    ],
    ['phone', (value: string) => /^\+?[0-9]{10,15}$/.test(value)]
  ])

  constructor() {
    super()
    // Create internal form element
    this.form = document.createElement('form')
    // Move all children to the form
    while (this.firstChild) {
      this.form.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to form
    Array.from(this.attributes).forEach((attr) => {
      if (attr.name !== 'is') {
        this.form.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.form)

    this.initConfig()
    this.initUIElements()
    this.addEventListeners()
  }

  /**
   * Initialize configuration from data attributes
   */
  private initConfig() {
    this.config = {
      apiKey: this.dataset.apiKey || '',
      listId: this.dataset.listId || '',
      isKlaviyoForm: Boolean(this.dataset.apiKey),
      apiRevision: this.dataset.apiRevision || '2025-01-15',
      endpoint: this.dataset.endpoint || 'https://a.klaviyo.com/client/subscriptions',
      formType: this.dataset.formType || 'custom',
      variantId: this.form.querySelector('[name="id"]')?.getAttribute('value') || '',
      productTitle: this.dataset.productTitle || ''
    }
  }

  /**
   * Initialize UI element references
   */
  private initUIElements() {
    const wrapper = this.closest('.product-form') || this
    this.uiElements = {
      wrapper,
      success: wrapper.querySelector(this.ui.selectors.successMessage),
      error: wrapper.querySelector(this.ui.selectors.errorMessage)
    }
  }

  /**
   * Handle form submission based on current state
   */
  private async handleSubmit(event: SubmitEvent) {
    event.preventDefault()

    const closestModal = this.closest('x-modal')
    const isDisabled = closestModal?.getAttribute('data-ui') === 'disabled'
    if (isDisabled && this.config.isKlaviyoForm) {
      await this.handleBackInStockNotification()
    } else {
      await this.handleProductAdd()
    }
  }

  /**
   * Handle product add to cart
   */
  private async handleProductAdd() {
    const formData = new FormData(this.form)
    const quantity = parseInt(formData.get('quantity')?.toString() || '0')
    const id = parseInt(formData.get('id')?.toString() || '0')
    if (!quantity || !id) {
      this.showMessage('error', 'Missing product ID or quantity')
      return
    }

    // Properly extract properties from form data
    const propertyObject: { [key: string]: string } = {}

    // Iterate through all form fields to find properties
    for (const [key, value] of formData.entries()) {
      // Check if the field name starts with 'properties['
      if (key.startsWith('properties[') && key.endsWith(']')) {
        // Extract the property name between brackets
        const propertyName = key.slice(11, -1)
        propertyObject[propertyName] = value.toString()
      }
    }
    try {
      const response = await cart.addLineItems([{ id, quantity, properties: propertyObject }])
      if (!response || response.error) {
        throw new Error(response?.error || 'Unknown error')
      }
    } catch (error: any) {
      console.error('Failed to add item to cart', error)
      this.showMessage('error', `Failed to add item to cart: ${error.message}`)
    }
  }

  /**
   * Handle back in stock notification subscription
   */
  private async handleBackInStockNotification() {
    const formData = new FormData(this.form)
    this.clearValidationErrors()

    // Validate the form
    const validation = this.validateForm(formData)
    if (!validation.valid) {
      this.showValidationErrors(validation.errors)
      return
    }

    // Process form data
    const data = Object.fromEntries(formData.entries())
    try {
      // Send data to Klaviyo
      const payload = this.prepareKlaviyoPayload(data)
      await this.sendToKlaviyo(payload)

      // Handle success
      this.reset()
      this.showMessage('success', 'We will email you if this product comes back in stock.')
      this.toggleSuccessElements()
    } catch (error: any) {
      console.error('API error:', error)
      this.showMessage(
        'error',
        'Error subscribing to back-in-stock notifications. Please try again or contact support.'
      )
    }
  }

  private reset() {
    this.form.reset()
  }

  /**
   * Validate all form fields
   */
  private validateForm(formData: FormData): { valid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {}
    let valid = true

    for (const [name, value] of formData.entries()) {
      if (typeof value === 'string' && this.validators.has(name)) {
        const isValid = this.validators.get(name)!(value)
        if (!isValid) {
          errors[name] = `Please enter a valid ${name}`
          valid = false
        }
      }
    }

    return { valid, errors }
  }

  /**
   * Display validation errors in the form
   */
  private showValidationErrors(errors: Record<string, string>) {
    for (const [field, message] of Object.entries(errors)) {
      const inputElement = this.form.querySelector(`[name="${field}"]`)
      if (inputElement) {
        const errorElement = document.createElement('div')
        errorElement.className = this.ui.classes.errorClass
        errorElement.textContent = message
        inputElement.parentNode?.insertBefore(errorElement, inputElement.nextSibling)
      }
    }
  }

  /**
   * Reset the form to initial state when the variant changes
   */
  private resetToInitialState(event: Event) {
    const target = event?.target as HTMLElement | null
    if (target?.getAttribute('name') !== 'id') return
    this.clearValidationErrors()
    this.resetDisplay()
  }

  private addEventListeners() {
    this.form.addEventListener('change', this.resetToInitialState.bind(this))
    this.form.addEventListener('submit', this.handleSubmit.bind(this))
  }

  private resetDisplay() {
    this.uiElements?.wrapper?.querySelectorAll(this.ui.selectors.showOnSuccess).forEach((el) => {
      el.removeAttribute(this.ui.selectors.uiAttribute)
    })

    this.uiElements?.wrapper?.querySelectorAll(this.ui.selectors.hideOnSuccess).forEach((el) => {
      el.setAttribute(this.ui.selectors.uiAttribute, this.ui.classes.visible)
    })
  }

  /**
   * Clear validation errors from the form
   */
  private clearValidationErrors() {
    this.form.querySelectorAll(`.${this.ui.classes.errorField}`).forEach((el) => el.remove())
  }

  /**
   * Prepare payload for Klaviyo API
   */
  private prepareKlaviyoPayload(formData: Record<string, any>) {
    // Extract profile attributes
    // Note: Custom properties cannot be added via the subscriptions endpoint
    // They must be added separately using the Profiles API
    const profileAttributes: Record<string, any> = {
      email: formData.email || undefined,
      first_name: formData.firstName || undefined,
      last_name: formData.lastName || undefined,
      phone_number: formData.phoneNumber || undefined
    }

    // Remove undefined values to keep payload clean
    Object.keys(profileAttributes).forEach((key) => {
      if (profileAttributes[key] === undefined) {
        delete profileAttributes[key]
      }
    })

    // Create base payload structure
    const payload = {
      data: {
        type: this.config.formType === 'custom' ? 'subscription' : 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          },
          channels: ['EMAIL']
        },
        relationships: {}
      }
    }

    // Add appropriate relationship based on form type
    if (this.config.formType === 'custom') {
      payload.data.relationships = {
        list: {
          data: {
            type: 'list',
            id: this.config.listId
          }
        }
      }
    } else {
      payload.data.relationships = {
        variant: {
          data: {
            type: 'catalog-variant',
            id: `$shopify:::$default:::${formData.id}`
          }
        }
      }
    }

    return payload
  }

  /**
   * Extract custom properties from form data
   */
  private extractCustomProperties(formData: Record<string, any>): Record<string, any> {
    const standardFields = [
      'email',
      'firstName',
      'lastName',
      'phoneNumber',
      'quantity',
      'id',
      'variantId',
      'productTitle'
    ]

    return Object.entries(formData)
      .filter(([key]) => !standardFields.includes(key))
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
  }

  /**
   * Send data to Klaviyo API
   */
  private async sendToKlaviyo(payload: any) {
    if (!this.config.apiKey) {
      throw new Error('Missing Klaviyo API key')
    }

    if (this.config.formType === 'custom' && !this.config.listId) {
      throw new Error('Missing Klaviyo list ID')
    }

    const response = await fetch(`${this.config.endpoint}?company_id=${this.config.apiKey}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: this.config.apiRevision,
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(`Klaviyo API error: ${response.status} ${response.statusText} ${errorData?.detail || ''}`)
    }

    return { status: response.status, message: 'Subscription successful' }
  }

  /**
   * Show success or error message
   */
  private showMessage(type: 'success' | 'error', message: string) {
    // Re-resolve lazily: the constructor may run before the children are parsed
    const selector = type === 'success' ? this.ui.selectors.successMessage : this.ui.selectors.errorMessage
    const element =
      (type === 'success' ? this.uiElements.success : this.uiElements.error) ||
      (this.uiElements.wrapper?.querySelector(selector) as HTMLElement | null)
    if (!element) return

    if (type === 'success') {
      this.uiElements.success = element
    } else {
      this.uiElements.error = element
    }

    const textElement = element.querySelector('span') || element
    if (textElement) {
      const messageToShow = type === 'success' ? element.getAttribute('data-message') || message : message
      textElement.textContent = messageToShow
    }

    // Show message
    element.setAttribute(this.ui.selectors.uiAttribute, this.ui.classes.visible)

    // Errors auto-dismiss; the success state is terminal (the buttons it replaced
    // stay hidden), so leave it on screen.
    if (type === 'error') {
      setTimeout(() => {
        element.removeAttribute(this.ui.selectors.uiAttribute)
      }, 5000)
    }
  }

  /**
   * Toggle visibility of success-related uiElements
   */
  private toggleSuccessElements() {
    const { wrapper } = this.uiElements
    if (!wrapper) return

    // Hide uiElements marked with hideOnSuccess
    wrapper.querySelectorAll(this.ui.selectors.hideOnSuccess).forEach((el) => {
      el.removeAttribute(this.ui.selectors.uiAttribute)
    })

    // Show uiElements marked with showOnSuccess
    wrapper.querySelectorAll(this.ui.selectors.showOnSuccess).forEach((el) => {
      el.setAttribute(this.ui.selectors.uiAttribute, this.ui.classes.visible)
    })
  }
}
